import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/shared/lib/supabase';
import { env } from '@/app/env';
import { logger } from '@/shared/lib/logger';
import { invalidateGroupFinancials, invalidateGroupsList } from '@/shared/lib/invalidate';

/**
 * Tiempo real de gastos y grupos.
 *
 * Se suscribe (un solo canal) a los cambios de `expenses`, `settlements`,
 * `groups` y `group_members`. Al recibir cualquier evento invalida las query
 * keys afectadas, de modo que los cambios de OTROS miembros aparecen en vivo
 * sin refrescar (además de los propios).
 *
 * Scoping: Realtime respeta las policies RLS (todas `is_group_member`), así que
 * el usuario solo recibe eventos de sus grupos. No hace falta filtro cliente.
 *
 * Complementa —no sustituye— la invalidación explícita por mutación y el
 * `refetchOnWindowFocus`: si el WebSocket cae (sleep del móvil, roaming), esos
 * dos mecanismos mantienen la UI al día.
 *
 * No-op si `VITE_ENABLE_REALTIME=false` o si aún no hay usuario logueado.
 *
 * @param participantId  id del participant logueado (== profile.id).
 */
export function useRealtimeSync(participantId: string | undefined): void {
  const qc = useQueryClient();

  useEffect(() => {
    if (!env.VITE_ENABLE_REALTIME) return;
    if (!participantId) return;

    // group_id del evento — en INSERT/UPDATE viene en `new`; en DELETE (con
    // REPLICA IDENTITY FULL) viene en `old`.
    const groupIdOf = (p: RealtimePayload): string | undefined =>
      p?.new?.group_id ?? p?.old?.group_id;

    const onFinancialChange = (p: RealtimePayload) => {
      const gid = groupIdOf(p);
      if (gid) invalidateGroupFinancials(qc, gid);
    };

    const channel = supabase
      .channel('realtime-sync')
      // Gastos: alta/edición/borrado → gastos + balances + inicio + analíticas.
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'expenses' },
        onFinancialChange,
      )
      // Liquidaciones: afectan a balances e inicio.
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'settlements' },
        onFinancialChange,
      )
      // Miembros: entrar/salir cambia la lista de grupos y los balances.
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'group_members' },
        (p: RealtimePayload) => {
          invalidateGroupsList(qc);
          onFinancialChange(p);
        },
      )
      // Grupos: crear/editar/borrar/archivar → listas de grupos + inicio.
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'groups' },
        () => invalidateGroupsList(qc),
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          logger.error('realtime-sync channel error', { code: status });
        }
      });

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [participantId, qc]);
}

interface RealtimePayload {
  eventType?: string;
  new?: { group_id?: string } | null;
  old?: { group_id?: string } | null;
}
